import React, { useState } from 'react';
import { Heart, Calendar, Clock, MapPin, Loader2, ArrowRight, Check, X, AlertTriangle, BookOpen, Info } from 'lucide-react';
import axios from 'axios';
import RelatedTools from '../components/RelatedTools';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import SEO from '../components/SEO';
import NorthIndianChart from '../components/charts/NorthIndianChart';

const API = process.env.REACT_APP_BACKEND_URL;

const SIGN_HI = ['मेष','वृषभ','मिथुन','कर्क','सिंह','कन्या','तुला','वृश्चिक','धनु','मकर','कुंभ','मीन'];

function ChartCard({ title, lagnaSign, planets, chartId, accent = 'gold' }) {
  return (
    <div className="glass-card p-3 md:p-4">
      <div className={`text-center text-[11px] tracking-[0.18em] uppercase mb-1.5 ${accent === 'gold' ? 'text-gold' : 'text-purple-300'}`}>
        {title}
      </div>
      <div className="flex justify-center">
        <NorthIndianChart
          size={240}
          lagnaSign={lagnaSign}
          planets={planets}
          chartId={chartId}
          showDegrees={false}
        />
      </div>
      <div className="text-center text-text-muted text-[11px] mt-1">
        लग्न: <span className="text-gold">{SIGN_HI[lagnaSign]}</span>
      </div>
    </div>
  );
}

function PersonCharts({ person, title, accent }) {
  if (!person) return null;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 px-2">
        <div className={`w-9 h-9 rounded-xl ${accent === 'blue' ? 'bg-blue-500/15 border-blue-500/30' : 'bg-pink-500/15 border-pink-500/30'} border flex items-center justify-center`}>
          <Heart className={`w-4 h-4 ${accent === 'blue' ? 'text-blue-400' : 'text-pink-400'}`} />
        </div>
        <div>
          <p className={`text-[11px] uppercase tracking-[0.2em] ${accent === 'blue' ? 'text-blue-300' : 'text-pink-300'}`}>{title}</p>
          <p className="text-text-main text-sm font-medium">{person.name}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <ChartCard title="D1 राशि" lagnaSign={person.lagna_sign_idx} planets={person.planets_d1} chartId={`${accent}-d1`} />
        <ChartCard title="D9 नवांश" lagnaSign={person.navamsa_lagna_sign_idx} planets={person.planets_d9} chartId={`${accent}-d9`} accent="purple" />
        <ChartCard title="चंद्र कुंडली" lagnaSign={person.moon_sign_idx} planets={person.planets_d1} chartId={`${accent}-chandra`} accent="purple" />
      </div>
    </div>
  );
}

function ManglikBlock({ label, data, mode }) {
  if (!data) return null;
  const dosh = mode === 'extended' ? data.is_manglik_extended : data.is_manglik_strict;
  return (
    <div className="bg-white/3 border border-white/8 rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-text-main text-sm font-medium">{label}</p>
        <span className={`text-xs px-2 py-1 rounded-full ${dosh ? 'bg-red-500/15 text-red-300 border border-red-500/30' : 'bg-green-500/15 text-green-300 border border-green-500/30'}`}>
          {dosh ? 'मांगलिक' : 'नहीं मांगलिक'}
        </span>
      </div>
      <div className="text-[12px] text-text-muted space-y-1">
        <div>लग्न से मंगल भाव: <span className="text-text-main">{data.lagna.mars_house}</span>{data.lagna.is_dosh && <span className="text-red-300 ml-1">(दोष)</span>}</div>
        <div>चन्द्र से मंगल भाव: <span className="text-text-main">{data.moon.mars_house}</span>{data.moon.is_dosh && <span className="text-red-300 ml-1">(दोष)</span>}</div>
        {mode === 'extended' && data.venus && (
          <div>शुक्र से मंगल भाव: <span className="text-text-main">{data.venus.mars_house}</span>{data.venus.is_dosh && <span className="text-yellow-300 ml-1">(दोष — आधुनिक)</span>}</div>
        )}
        <div className="pt-1">मंगल राशि-स्थिति: <span className="text-text-main">
          {data.mars_dignity === 'exalted' ? 'उच्च' : data.mars_dignity === 'own' ? 'स्वराशि' : data.mars_dignity === 'debilitated' ? 'नीच' : 'सामान्य'}
        </span></div>
      </div>
    </div>
  );
}

export default function GunMilanPage() {
  const [formData, setFormData] = useState({
    boy_name: '', boy_birth_date: '', boy_birth_time: '', boy_birth_place: '',
    girl_name: '', girl_birth_date: '', girl_birth_time: '', girl_birth_place: ''
  });
  const [mode, setMode] = useState('strict');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/api/gun-milan/advanced?mode=${mode}`, formData);
      setResult(res.data);
    } catch {
      setError('गणना में त्रुटि। कृपया पुनः प्रयास करें।');
    } finally {
      setLoading(false);
    }
  };

  const update = (field, val) => setFormData(prev => ({ ...prev, [field]: val }));

  const switchMode = async (newMode) => {
    if (newMode === mode) return;
    setMode(newMode);
    if (result) {
      setLoading(true);
      try {
        const res = await axios.post(`${API}/api/gun-milan/advanced?mode=${newMode}`, formData);
        setResult(res.data);
      } catch { /* ignore */ }
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Advanced Gun Milan | Professional Kundli Matching | अष्टकूट + मंगल दोष विश्लेषण"
        breadcrumbs={[
          { name: 'होम', path: '/' },
          { name: 'गुण मिलान', path: '/gun-milan' },
        ]}
        description="Professional Vedic Gun Milan calculator with full Ashtakoot, Manglik analysis (Lagna/Moon/Venus), Nadi & Bhakoot Parihar, plus side-by-side D1, D9 and Chandra Kundli comparison. By Vivek Mudgal Astrologer."
        path="/gun-milan"
        keywords="advanced gun milan, kundli matching, ashtakoota, manglik dosha, nadi dosha, bhakoot, marriage compatibility, Vivek Mudgal"
        jsonLd={{ '@context': 'https://schema.org', '@type': 'Service', name: 'Advanced Gun Milan', provider: { '@type': 'Person', name: 'Vivek Mudgal' }, serviceType: 'Astrology Consultation', areaServed: 'India' }}
      />
      <Header />
      <FloatingActions />

      <section className="pt-28 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 cosmic-bg" />
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-gold/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/3 left-0 w-72 h-72 bg-purple/8 rounded-full blur-[100px]" />

        <div className="container-custom relative z-10">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="text-xs tracking-[0.3em] uppercase text-gold font-semibold font-body mb-4">
              Advanced Kundli Milan · अष्टकूट + मंगल दोष
            </p>
            <h1 className="font-heading text-4xl md:text-5xl font-medium text-text-main mb-4 tracking-tight">
              गुण <span className="text-gold-gradient">मिलान</span>
            </h1>
            <p className="text-text-muted text-sm">
              Side-by-side D1 + D9 + चंद्र कुंडली · सम्पूर्ण अष्टकूट विश्लेषण · पाराशरीय मंगल दोष परिहार सहित
            </p>
            <div className="divine-divider mt-6 max-w-xs mx-auto" />
          </div>

          {!result ? (
            <div className="max-w-4xl mx-auto">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Mode Selector */}
                <div className="glass-card p-4">
                  <p className="text-text-muted text-xs uppercase tracking-wider mb-3">मंगल दोष विश्लेषण मोड</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => setMode('strict')}
                      className={`px-4 py-2.5 rounded-xl text-sm transition-all ${mode === 'strict' ? 'bg-gold text-background font-semibold' : 'bg-white/5 text-text-muted border border-white/10'}`}
                      data-testid="gunmilan-mode-strict">
                      Strict Parashari (लग्न + चन्द्र)
                    </button>
                    <button type="button" onClick={() => setMode('extended')}
                      className={`px-4 py-2.5 rounded-xl text-sm transition-all ${mode === 'extended' ? 'bg-gold text-background font-semibold' : 'bg-white/5 text-text-muted border border-white/10'}`}
                      data-testid="gunmilan-mode-extended">
                      Extended (+ शुक्र)
                    </button>
                  </div>
                </div>

                {/* Boy Details */}
                <div className="glass-card p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-500/5 flex items-center justify-center border border-blue-500/15">
                      <Heart className="w-5 h-5 text-blue-400" />
                    </div>
                    <h3 className="font-heading text-lg text-text-main">वर का विवरण / Boy&apos;s Details</h3>
                  </div>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm text-text-muted mb-2">नाम / Name *</label>
                      <input type="text" value={formData.boy_name} onChange={e => update('boy_name', e.target.value)} placeholder="वर का नाम" required data-testid="gunmilan-boy-name" />
                    </div>
                    <div>
                      <label className="block text-sm text-text-muted mb-2">जन्म तिथि / Birth Date *</label>
                      <div className="relative">
                        <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                        <input type="date" value={formData.boy_birth_date} onChange={e => update('boy_birth_date', e.target.value)} required className="pl-12" data-testid="gunmilan-boy-date" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-text-muted mb-2">जन्म समय / Birth Time</label>
                      <div className="relative">
                        <Clock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                        <input type="time" value={formData.boy_birth_time} onChange={e => update('boy_birth_time', e.target.value)} className="pl-12" data-testid="gunmilan-boy-time" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-text-muted mb-2">जन्म स्थान / Birth Place</label>
                      <div className="relative">
                        <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                        <input type="text" value={formData.boy_birth_place} onChange={e => update('boy_birth_place', e.target.value)} placeholder="City / शहर" className="pl-12" data-testid="gunmilan-boy-place" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Girl Details */}
                <div className="glass-card p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500/20 to-pink-500/5 flex items-center justify-center border border-pink-500/15">
                      <Heart className="w-5 h-5 text-pink-400" />
                    </div>
                    <h3 className="font-heading text-lg text-text-main">कन्या का विवरण / Girl&apos;s Details</h3>
                  </div>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm text-text-muted mb-2">नाम / Name *</label>
                      <input type="text" value={formData.girl_name} onChange={e => update('girl_name', e.target.value)} placeholder="कन्या का नाम" required data-testid="gunmilan-girl-name" />
                    </div>
                    <div>
                      <label className="block text-sm text-text-muted mb-2">जन्म तिथि / Birth Date *</label>
                      <div className="relative">
                        <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                        <input type="date" value={formData.girl_birth_date} onChange={e => update('girl_birth_date', e.target.value)} required className="pl-12" data-testid="gunmilan-girl-date" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-text-muted mb-2">जन्म समय / Birth Time</label>
                      <div className="relative">
                        <Clock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                        <input type="time" value={formData.girl_birth_time} onChange={e => update('girl_birth_time', e.target.value)} className="pl-12" data-testid="gunmilan-girl-time" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-text-muted mb-2">जन्म स्थान / Birth Place</label>
                      <div className="relative">
                        <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                        <input type="text" value={formData.girl_birth_place} onChange={e => update('girl_birth_place', e.target.value)} placeholder="City / शहर" className="pl-12" data-testid="gunmilan-girl-place" />
                      </div>
                    </div>
                  </div>
                </div>

                {error && <p className="text-red-400 text-sm text-center" data-testid="gunmilan-error">{error}</p>}

                <button type="submit" disabled={loading || !formData.boy_name || !formData.boy_birth_date || !formData.girl_name || !formData.girl_birth_date}
                  className="btn-gold w-full flex items-center justify-center gap-2 disabled:opacity-50 text-lg py-4" data-testid="gunmilan-submit-btn">
                  {loading ? (<><Loader2 className="w-5 h-5 animate-spin" /> गणना हो रही है...</>) : (<><Heart className="w-5 h-5" /> गुण मिलान करें</>)}
                </button>
              </form>
            </div>
          ) : (
            <div className="max-w-6xl mx-auto space-y-8 animate-fade-in-up">
              {/* Score Card */}
              <div className="glass-card p-6 md:p-8 text-center">
                <h2 className="font-heading text-3xl text-text-main mb-2">गुण मिलान रिपोर्ट</h2>
                <p className="text-text-muted text-sm mb-6">{result.boy.name} & {result.girl.name}</p>
                <div className="w-36 h-36 mx-auto rounded-full border-4 flex items-center justify-center mb-4"
                  style={{ borderColor: result.verdict_color === 'green' ? '#22c55e' : result.verdict_color === 'yellow' ? '#eab308' : '#ef4444' }}>
                  <div>
                    <p className="font-heading text-4xl text-text-main" data-testid="gunmilan-total-points">{result.total_points}</p>
                    <p className="text-text-muted text-xs">/ {result.max_points} गुण</p>
                  </div>
                </div>
                <p className={`font-heading text-xl mb-1 ${result.verdict_color === 'green' ? 'text-green-400' : result.verdict_color === 'yellow' ? 'text-yellow-400' : 'text-red-400'}`} data-testid="gunmilan-verdict">{result.verdict_hi}</p>
                <p className="text-text-muted text-sm">{result.verdict_en}</p>

                {/* Mode toggle in results */}
                <div className="inline-flex bg-white/5 rounded-full p-1 mt-6 border border-white/10">
                  <button onClick={() => switchMode('strict')} className={`px-4 py-1.5 rounded-full text-xs ${result.mode === 'strict' ? 'bg-gold text-background font-semibold' : 'text-text-muted'}`} data-testid="result-mode-strict">Strict (लग्न+चन्द्र)</button>
                  <button onClick={() => switchMode('extended')} className={`px-4 py-1.5 rounded-full text-xs ${result.mode === 'extended' ? 'bg-gold text-background font-semibold' : 'text-text-muted'}`} data-testid="result-mode-extended">+ शुक्र</button>
                </div>
              </div>

              {/* Side-by-side Charts */}
              <div className="space-y-6" data-testid="gunmilan-charts">
                <h3 className="font-heading text-xl text-text-main text-center">कुंडली तुलना · Side-by-Side Charts</h3>
                <div className="grid lg:grid-cols-2 gap-6">
                  <PersonCharts person={result.boy_charts} title="वर / Boy" accent="blue" />
                  <PersonCharts person={result.girl_charts} title="कन्या / Girl" accent="pink" />
                </div>
              </div>

              {/* Comparison Table */}
              <div className="glass-card p-5 md:p-6" data-testid="gunmilan-comparison">
                <h3 className="font-heading text-lg text-text-main mb-4">ग्रह तुलना · Quick Comparison</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-text-muted text-xs uppercase tracking-wider border-b border-white/10">
                        <th className="py-2 pr-4">विषय</th>
                        <th className="py-2 pr-4 text-blue-300">वर</th>
                        <th className="py-2 text-pink-300">कन्या</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ['लग्न', result.comparison.lagna],
                        ['चंद्र राशि', result.comparison.moon_rashi],
                        ['राशि स्वामी', result.comparison.rashi_lord],
                        ['नक्षत्र', result.comparison.nakshatra],
                        ['योनि', result.comparison.yoni],
                        ['गण', result.comparison.gana],
                        ['नाड़ी', result.comparison.nadi],
                        ['वर्ण', result.comparison.varna],
                      ].map(([k, v]) => (
                        <tr key={k} className="border-b border-white/5 last:border-0">
                          <td className="py-2.5 pr-4 text-text-muted">{k}</td>
                          <td className="py-2.5 pr-4 text-text-main">{v.boy}</td>
                          <td className="py-2.5 text-text-main">{v.girl}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Ashtakoot Detail */}
              <div className="glass-card p-5 md:p-6">
                <h3 className="font-heading text-lg text-text-main mb-4">अष्टकूट विस्तृत विश्लेषण</h3>
                <div className="space-y-3">
                  {result.ashtakoot.map((g, i) => {
                    const pct = (g.obtained / g.max);
                    return (
                      <div key={i} className="bg-white/3 rounded-xl border border-white/8 p-4" data-testid={`koot-${g.name_en.toLowerCase().replace(/\s+/g, '-')}`}>
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center ${pct >= 0.5 ? 'bg-green-500/15' : 'bg-red-500/15'}`}>
                              {pct >= 0.5 ? <Check className="w-4 h-4 text-green-400" /> : <X className="w-4 h-4 text-red-400" />}
                            </div>
                            <div>
                              <p className="text-text-main text-sm font-medium">{g.name} <span className="text-text-muted text-xs">({g.name_en})</span></p>
                              <p className="text-text-muted text-[11px]">{g.significance}</p>
                            </div>
                          </div>
                          <p className={`font-heading text-lg ${pct >= 0.5 ? 'text-green-400' : 'text-red-400'} shrink-0`}>{g.obtained}<span className="text-text-muted text-sm">/{g.max}</span></p>
                        </div>
                        <div className="text-[12px] grid sm:grid-cols-2 gap-2 mt-2">
                          <div className="text-blue-300">वर: <span className="text-text-main">{g.boy_value}</span></div>
                          <div className="text-pink-300">कन्या: <span className="text-text-main">{g.girl_value}</span></div>
                        </div>
                        <p className="text-text-muted text-[12px] mt-2 leading-relaxed">{g.reason}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Bhakoot + Nadi Parihar */}
              {(result.ashtakoot.find(k => k.name_en === 'Bhakoot').obtained === 0 || result.ashtakoot.find(k => k.name_en === 'Nadi').obtained === 0) && (
                <div className="glass-card p-5 md:p-6 border-yellow-500/20">
                  <div className="flex items-center gap-2 mb-3">
                    <Info className="w-4 h-4 text-yellow-400" />
                    <h3 className="font-heading text-lg text-text-main">दोष परिहार · Dosh Cancellation</h3>
                  </div>
                  {result.ashtakoot.find(k => k.name_en === 'Bhakoot').obtained === 0 && (
                    <div className="mb-3" data-testid="bhakoot-parihar">
                      <p className="text-text-main text-sm font-medium mb-1">भकूट दोष परिहार:</p>
                      <p className={`text-[13px] ${result.bhakoot_parihar.cancelled ? 'text-green-300' : 'text-text-muted'}`}>
                        {result.bhakoot_parihar.cancelled ? '✓ ' : '✗ '}{result.bhakoot_parihar.reason}
                      </p>
                    </div>
                  )}
                  {result.ashtakoot.find(k => k.name_en === 'Nadi').obtained === 0 && (
                    <div data-testid="nadi-parihar">
                      <p className="text-text-main text-sm font-medium mb-1">नाड़ी दोष परिहार:</p>
                      <p className={`text-[13px] ${result.nadi_parihar.cancelled ? 'text-green-300' : 'text-text-muted'}`}>
                        {result.nadi_parihar.cancelled ? '✓ ' : '✗ '}{result.nadi_parihar.reason}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Manglik Analysis */}
              <div className="glass-card p-5 md:p-6" data-testid="manglik-analysis">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-lg text-text-main flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-orange-400" /> मंगल दोष विश्लेषण
                  </h3>
                  <span className="text-[11px] text-text-muted">Mode: {result.mode === 'strict' ? 'Strict Parashari' : 'Extended (+ शुक्र)'}</span>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <ManglikBlock label={`वर: ${result.boy.name}`} data={result.mode === 'extended' ? result.manglik.boy_extended : result.manglik.boy_strict} mode={result.mode} />
                  <ManglikBlock label={`कन्या: ${result.girl.name}`} data={result.mode === 'extended' ? result.manglik.girl_extended : result.manglik.girl_strict} mode={result.mode} />
                </div>

                {result.mode === 'extended' && result.manglik.boy_extended?.extended_disclaimer && (
                  <p className="text-yellow-300 text-[12px] mt-4 bg-yellow-500/5 border border-yellow-500/20 rounded-lg p-3" data-testid="extended-disclaimer">
                    ⚠️ {result.manglik.boy_extended.extended_disclaimer}
                  </p>
                )}

                {/* Cancellation */}
                <div className="mt-5 bg-white/3 border border-white/8 rounded-xl p-4" data-testid="manglik-cancellation">
                  <p className="text-text-main text-sm font-medium mb-2">परिहार स्थिति:
                    <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${result.manglik.cancellation.cancelled ? 'bg-green-500/15 text-green-300 border border-green-500/30' : 'bg-text-muted/10 text-text-muted'}`}>
                      {result.manglik.cancellation.cancelled ? 'दोष परिहार लागू' : 'परिहार लागू नहीं'}
                    </span>
                  </p>
                  <ul className="text-[12px] text-text-muted space-y-1 list-disc pl-5">
                    {result.manglik.cancellation.reasons.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </div>
              </div>

              {/* Manglik Source Shlokas */}
              <div className="glass-card p-5 md:p-6" data-testid="manglik-sources">
                <div className="flex items-center gap-2 mb-4">
                  <BookOpen className="w-4 h-4 text-gold" />
                  <h3 className="font-heading text-lg text-text-main">शास्त्रीय प्रमाण · Manglik Sources</h3>
                </div>
                <div className="space-y-4">
                  {result.manglik_sources.map((s, i) => (
                    <div key={i} className="border-l-2 border-gold/40 pl-4">
                      <p className="text-gold text-sm font-medium">{s.reference}</p>
                      <p className="text-text-muted text-[11px] mb-2">{s.grantha} · {s.adhyaya_shloka}</p>
                      {s.sanskrit !== '—' && (
                        <pre className="text-text-main text-[13px] font-serif whitespace-pre-wrap leading-relaxed bg-white/3 p-3 rounded-lg border border-white/5 mb-2">{s.sanskrit}</pre>
                      )}
                      <p className="text-text-muted text-[12px]">{s.meaning_hi}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="bg-gradient-to-r from-gold to-gold-dark rounded-2xl p-8 text-background text-center">
                <h4 className="font-heading text-2xl mb-3">विस्तृत परामर्श चाहिए?</h4>
                <p className="mb-6 opacity-90 text-sm">Vivek Mudgal Astrologer से personalised remedies, मंगल शांति विधि एवं विवाह मुहूर्त पाएं।</p>
                <div className="flex flex-wrap gap-4 justify-center">
                  <a href="/#book-consultation" className="bg-background text-gold px-6 py-3 rounded-full font-semibold hover:bg-surface transition-colors inline-flex items-center gap-2">परामर्श लें <ArrowRight className="w-4 h-4" /></a>
                  <button onClick={() => setResult(null)} className="border-2 border-background text-background px-6 py-3 rounded-full font-semibold hover:bg-background/10 transition-colors" data-testid="gunmilan-recalculate">पुनः मिलान करें</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <RelatedTools />
      <Footer />
    </div>
  );
}
